import { DecisionActionResult } from "../../models/home";
import { HomeDecisionActionUxService } from "../../services/home-ia/home-decision-action-ux.service";
import { HomeDashboardStore } from "./home.component.dashboard-store";
import { HomeAutoRefreshTarget } from "./home.component.state";

/** Selects the minimal dashboard slices that require server refresh. */
export class HomeDecisionRefreshPlanner {
  constructor(private readonly store: HomeDashboardStore, private readonly actionUx: HomeDecisionActionUxService) {}

  resolve(decisionId: string, result: DecisionActionResult): readonly HomeAutoRefreshTarget[] {
    const targets = result.refreshHints?.length
      ? this.mapHints(result.refreshHints, decisionId)
      : this.getFallbackTargets(decisionId, result);
    return Array.from(this.prunePatchedTargets(targets, result));
  }
  hasHint(hints: ReadonlySet<string>, ...candidates: string[]): boolean {
    return candidates.some((candidate) => hints.has(candidate));
  }
  mapHints(hints: readonly string[], decisionId: string): ReadonlySet<HomeAutoRefreshTarget> {
    const normalized = new Set(hints.map((hint) => hint.toLowerCase()));
    const targets = new Set<HomeAutoRefreshTarget>();
    if (this.hasHint(normalized, "overview", "summary", "counts")) targets.add("overview");
    if (this.hasHint(normalized, "next", "hero")) targets.add("next");
    if (this.hasHint(normalized, "list", "decisions")) { targets.add("list"); targets.add("calendar"); }
    if (this.shouldRefreshDetail(decisionId) && this.hasHint(normalized, "detail", "decision")) targets.add("detail");
    if (this.hasHint(normalized, "activity", "history")) targets.add("activity");
    if (this.hasHint(normalized, "dates")) targets.add("dates");
    if (this.hasHint(normalized, "pulse")) targets.add("pulse");
    if (this.hasHint(normalized, "metrics")) targets.add("metrics");
    return targets;
  }
  getFallbackTargets(decisionId: string, result: DecisionActionResult): ReadonlySet<HomeAutoRefreshTarget> {
    const targets = new Set<HomeAutoRefreshTarget>();
    if (result.actionResultType === "rejected") {
      if (this.shouldRefreshDetail(decisionId)) targets.add("detail");
      return targets;
    }
    if (result.actionResultType === "inspection_only" || result.actionResultType === "replay" ||
        this.actionUx.shouldShowReevaluationPending(result)) return targets;
    if (!result.updatedSummary) targets.add("overview");
    if (!this.canPatchDecisionSlicesLocally(result)) {
      targets.add("list"); targets.add("calendar"); targets.add("next");
      if (this.shouldRefreshDetail(decisionId)) targets.add("detail");
    }
    if (this.needsDatesRefresh(result)) targets.add("dates");
    return targets;
  }
  prunePatchedTargets(targets: ReadonlySet<HomeAutoRefreshTarget>, result: DecisionActionResult): Set<HomeAutoRefreshTarget> {
    const filtered = new Set(targets);
    if (result.updatedSummary) filtered.delete("overview");
    if (this.canPatchDecisionSlicesLocally(result)) {
      ["list", "calendar", "next", "detail"].forEach((target) => filtered.delete(target as HomeAutoRefreshTarget));
    }
    return filtered;
  }
  shouldPatchLocally(result: DecisionActionResult): boolean {
    return !!result.updatedSummary || this.canPatchDecisionSlicesLocally(result);
  }
  canPatchDecisionSlicesLocally(result: DecisionActionResult): boolean {
    return !!result.updatedDecision || result.availableActions !== undefined;
  }
  needsDatesRefresh(result: DecisionActionResult): boolean {
    const action = result.action?.toLowerCase() ?? "";
    return action.includes("snooze") || action.includes("wait");
  }
  shouldRefreshDetail(decisionId: string): boolean {
    const selected = this.store.selectedDecisionIdSubject.value;
    return !selected || selected === decisionId;
  }
}
